/* =========================================================================
   WEAR CLOVERSE — Wishlist
   localStorage-backed, zero-dependency. Two custom elements:
     <wishlist-button data-handle data-title data-url data-image data-price>
     <wishlist-grid>  (renders saved items on the wishlist page)
   Plus an auto-injected count badge on any link to /pages/wishlist.
   All product data needed for the page is stored on toggle, so the page
   renders instantly with no network calls.
   ========================================================================= */

const KEY = 'cloverse:wishlist:v1';
const CHANGE = 'cloverse:wishlist:change';

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (e) {
    /* storage full / disabled — fail silently */
  }
  document.dispatchEvent(new CustomEvent(CHANGE, { detail: { list } }));
}

const store = {
  all: read,
  has(handle) {
    return read().some((i) => i.handle === handle);
  },
  toggle(item) {
    const list = read();
    const idx = list.findIndex((i) => i.handle === item.handle);
    if (idx > -1) {
      list.splice(idx, 1);
      write(list);
      return false;
    }
    list.unshift(item);
    write(list);
    return true;
  },
  remove(handle) {
    write(read().filter((i) => i.handle !== handle));
  },
};

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const HEART =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.9-9A4.6 4.6 0 0 1 12 6.5a4.6 4.6 0 0 1 9.9 5c-2.4 4.4-9.9 9-9.9 9Z"/></svg>';

/* ------------------------------------------------------------ <wishlist-button> */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    if (!this.handle || this._built) return;
    this._built = true;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'cloverse-wish';
    this.button.innerHTML = HEART;
    this.appendChild(this.button);

    this.sync();
    this.button.addEventListener('click', (e) => this.onClick(e));
    this._onChange = () => this.sync();
    document.addEventListener(CHANGE, this._onChange);
  }

  disconnectedCallback() {
    if (this._onChange) document.removeEventListener(CHANGE, this._onChange);
  }

  onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const active = store.toggle({
      handle: this.handle,
      id: this.dataset.productId || '',
      title: this.dataset.title || '',
      url: this.dataset.url || '',
      image: this.dataset.image || '',
      price: this.dataset.price || '',
    });
    if (active) {
      this.button.classList.remove('is-pop');
      // reflow to restart the pop animation
      void this.button.offsetWidth;
      this.button.classList.add('is-pop');
    }
  }

  sync() {
    const active = store.has(this.handle);
    this.button.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.button.setAttribute(
      'aria-label',
      active ? 'Remove from wishlist' : 'Add to wishlist'
    );
    this.button.setAttribute('title', active ? 'In your wishlist' : 'Save to wishlist');
  }
}

/* -------------------------------------------------------------- <wishlist-grid> */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.render();
    this._onChange = () => this.render();
    document.addEventListener(CHANGE, this._onChange);
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish-remove]');
      if (btn) {
        e.preventDefault();
        store.remove(btn.getAttribute('data-wish-remove'));
      }
    });
  }

  disconnectedCallback() {
    if (this._onChange) document.removeEventListener(CHANGE, this._onChange);
  }

  render() {
    const list = store.all();
    if (!list.length) {
      const shopUrl = this.getAttribute('data-shop-url') || '/collections/all';
      this.innerHTML =
        '<div class="cloverse-wishlist-empty">' +
        '<span class="cloverse-wishlist-empty__glyph">' + HEART + '</span>' +
        '<p class="cloverse-wishlist-empty__title">Your wishlist is empty</p>' +
        '<p class="cloverse-wishlist-empty__body">Tap the heart on any piece to save it here.</p>' +
        '<a class="button button--arrow" href="' + shopUrl + '">Browse the drop' +
        '<span class="cloverse-arrow"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span></a>' +
        '</div>';
      return;
    }

    const cards = list
      .map((i) => {
        const img = i.image
          ? '<img src="' + esc(i.image) + '" alt="" loading="lazy" class="cloverse-wishlist-card__img">'
          : '<span class="cloverse-wishlist-card__img cloverse-wishlist-card__img--empty"></span>';
        return (
          '<article class="cloverse-wishlist-card">' +
          '<a class="cloverse-wishlist-card__media" href="' + esc(i.url) + '">' + img + '</a>' +
          '<button type="button" class="cloverse-wish is-active cloverse-wishlist-card__remove" ' +
          'data-wish-remove="' + esc(i.handle) + '" aria-label="Remove from wishlist">' + HEART + '</button>' +
          '<a class="cloverse-wishlist-card__title" href="' + esc(i.url) + '">' + esc(i.title) + '</a>' +
          (i.price ? '<span class="cloverse-wishlist-card__price">' + esc(i.price) + '</span>' : '') +
          '<a class="button button--arrow cloverse-card__view" href="' + esc(i.url) + '">View item' +
          '<span class="cloverse-arrow"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span></a>' +
          '</article>'
        );
      })
      .join('');

    this.innerHTML = '<div class="cloverse-wishlist-grid">' + cards + '</div>';
  }
}

/* ------------------------------------------------------- Header count badge */
function initBadges() {
  const links = document.querySelectorAll('a[href*="/pages/wishlist"]');
  if (!links.length) return;
  const badges = [];
  links.forEach((link) => {
    let badge = link.querySelector('[data-wishlist-count]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'cloverse-wishlist-count';
      badge.setAttribute('data-wishlist-count', '');
      link.appendChild(badge);
    }
    badges.push(badge);
  });
  const update = () => {
    const n = store.all().length;
    badges.forEach((b) => {
      b.textContent = n;
      b.toggleAttribute('data-visible', n > 0);
    });
  };
  document.addEventListener(CHANGE, update);
  update();
}

/* ------------------------------------------------------------------- Boot */
if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBadges, { once: true });
} else {
  initBadges();
}
